import { ChangeEvent, FormEvent, useEffect, useMemo, useRef, useState } from 'react'

type UploadSide = 'front' | 'back'

type Resp<T> = {
  err?: number
  msg?: string
  data?: T
}

type PresignedData = {
  endpoint: string
  prefix?: string
  bucket: string
  key: string
  presigned: Record<string, string | number | boolean>
}

type ImageItem = {
  file: File
  previewUrl: string
  compressedBytes: number
  width: number
  height: number
}

type Notice = {
  tone: 'success' | 'error' | 'info'
  text: string
}

const API_BASE = import.meta.env.VITE_OMSK_API_BASE || 'https://omsk-api.ztokr.com'
const MAX_IMAGE_EDGE = 1600
const TARGET_BYTES = 1.2 * 1024 * 1024
const HARD_LIMIT_BYTES = 2.5 * 1024 * 1024

function bytesLabel(bytes: number) {
  if (bytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(bytes / 1024))} KB`
  }
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function readUrlParam(...names: string[]) {
  const params = new URLSearchParams(window.location.search)
  for (const name of names) {
    const value = params.get(name)
    if (value) return value
  }

  const hashQuery = window.location.hash.split('?')[1]
  if (hashQuery) {
    const hashParams = new URLSearchParams(hashQuery)
    for (const name of names) {
      const value = hashParams.get(name)
      if (value) return value
    }
  }

  return ''
}

async function postJson<T>(path: string, body: unknown, timeoutMs = 20000): Promise<T> {
  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs)

  try {
    const response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    })
    const text = await response.text()
    const payload = text ? (JSON.parse(text) as Resp<T>) : {}

    if (!response.ok || payload.err) {
      throw new Error(payload.msg || `接口返回异常：HTTP ${response.status}`)
    }

    return payload.data as T
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error('接口请求超时，请稍后重试')
    }
    if (error instanceof SyntaxError) {
      throw new Error('接口返回格式异常，请稍后重试')
    }
    throw error
  } finally {
    window.clearTimeout(timeout)
  }
}

function imageFromFile(file: File) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve(image)
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('当前浏览器无法读取该图片，请改用 JPG/PNG 或重新拍照'))
    }
    image.src = url
  })
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('图片压缩失败，请重新选择图片'))
        }
      },
      'image/jpeg',
      quality,
    )
  })
}

async function compressImage(file: File): Promise<ImageItem> {
  if (!file.type.startsWith('image/')) {
    throw new Error('请选择身份证照片图片文件')
  }

  const image = await imageFromFile(file)
  const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(image.naturalWidth, image.naturalHeight))
  const width = Math.max(1, Math.round(image.naturalWidth * scale))
  const height = Math.max(1, Math.round(image.naturalHeight * scale))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('当前浏览器不支持图片压缩')
  }

  ctx.drawImage(image, 0, 0, width, height)

  let quality = 0.82
  let blob = await canvasToBlob(canvas, quality)
  while (blob.size > TARGET_BYTES && quality > 0.62) {
    quality -= 0.08
    blob = await canvasToBlob(canvas, quality)
  }

  if (blob.size > HARD_LIMIT_BYTES) {
    throw new Error(`图片仍然过大（${bytesLabel(blob.size)}），请裁剪后再上传`)
  }

  const basename = file.name.replace(/\.[^.]+$/, '') || 'ident-card'
  const compressedFile = new File([blob], `${basename}.jpg`, { type: 'image/jpeg' })

  return {
    file: compressedFile,
    previewUrl: URL.createObjectURL(blob),
    compressedBytes: blob.size,
    width,
    height,
  }
}

async function uploadToOss(file: File) {
  const data = await postJson<PresignedData>('/api/oss/presigned', { filename: file.name })
  if (!data?.endpoint || !data.bucket || !data.key || !data.presigned) {
    throw new Error('OSS 预签名信息不完整，请稍后重试')
  }

  const formData = new FormData()
  for (const [key, value] of Object.entries(data.presigned)) {
    formData.append(key, String(value))
  }
  formData.append('file', file, file.name)

  const response = await fetch(`${data.endpoint}/${data.bucket}`, {
    method: 'POST',
    body: formData,
  })
  if (!response.ok) {
    throw new Error(`OSS 上传失败：HTTP ${response.status}`)
  }

  const prefix = data.prefix || `${data.endpoint}/${data.bucket}`
  return `${prefix}/${data.key}`
}

function UploadCard({
  title,
  item,
  disabled,
  onPick,
}: {
  title: string
  item: ImageItem | null
  disabled: boolean
  onPick: (event: ChangeEvent<HTMLInputElement>) => void
}) {
  const [sourcePickerOpen, setSourcePickerOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const albumInputRef = useRef<HTMLInputElement>(null)
  const cameraOptionRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!sourcePickerOpen) return

    cameraOptionRef.current?.focus()
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSourcePickerOpen(false)
        triggerRef.current?.focus()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [sourcePickerOpen])

  function chooseSource(input: HTMLInputElement | null) {
    setSourcePickerOpen(false)
    input?.click()
  }

  function closeSourcePicker() {
    setSourcePickerOpen(false)
    triggerRef.current?.focus()
  }

  return (
    <section className="upload-card">
      <div className="upload-card__copy">
        <p>{title}</p>
        <span>{item ? `${item.width}×${item.height} · ${bytesLabel(item.compressedBytes)}` : '拍照或从相册选择'}</span>
      </div>

      <button
        ref={triggerRef}
        className="picker"
        type="button"
        aria-label={`${item ? '重新上传' : '上传'}${title}`}
        disabled={disabled}
        onClick={() => setSourcePickerOpen(true)}
      >
        {item ? (
          <img src={item.previewUrl} alt={`${title}预览`} />
        ) : (
          <span aria-hidden="true">+</span>
        )}
      </button>

      <input
        ref={cameraInputRef}
        className="file-input"
        type="file"
        accept="image/*"
        capture="environment"
        disabled={disabled}
        onChange={onPick}
      />
      <input
        ref={albumInputRef}
        className="file-input"
        type="file"
        accept="image/*"
        disabled={disabled}
        onChange={onPick}
      />

      {sourcePickerOpen ? (
        <div className="source-picker-backdrop" onClick={closeSourcePicker}>
          <div
            className="source-picker-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`source-picker-${title}`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="source-picker-heading">
              <h2 id={`source-picker-${title}`}>上传{title}</h2>
              <p>请选择照片来源</p>
            </div>
            <div className="source-picker-actions">
              <button ref={cameraOptionRef} type="button" onClick={() => chooseSource(cameraInputRef.current)}>
                拍照
              </button>
              <button type="button" onClick={() => chooseSource(albumInputRef.current)}>
                从相册选择
              </button>
              <button className="source-picker-cancel" type="button" onClick={closeSourcePicker}>
                取消
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  )
}

export default function App() {
  const [waybill, setWaybill] = useState(() => readUrlParam('bill', 'waybill'))
  const [front, setFront] = useState<ImageItem | null>(null)
  const [back, setBack] = useState<ImageItem | null>(null)
  const [busy, setBusy] = useState(false)
  const [processingSide, setProcessingSide] = useState<UploadSide | null>(null)
  const [notice, setNotice] = useState<Notice | null>(null)
  const [stepText, setStepText] = useState('')
  const frontRef = useRef<ImageItem | null>(null)
  const backRef = useRef<ImageItem | null>(null)

  useEffect(() => {
    frontRef.current = front
  }, [front])

  useEffect(() => {
    backRef.current = back
  }, [back])

  useEffect(() => {
    return () => {
      if (frontRef.current) URL.revokeObjectURL(frontRef.current.previewUrl)
      if (backRef.current) URL.revokeObjectURL(backRef.current.previewUrl)
    }
  }, [])

  const canSubmit = useMemo(() => Boolean(waybill.trim() && front && back && !busy && !processingSide), [
    waybill,
    front,
    back,
    busy,
    processingSide,
  ])

  async function handlePick(side: UploadSide, event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    setNotice({ tone: 'info', text: '正在处理图片，请稍等' })
    setProcessingSide(side)
    try {
      const item = await compressImage(file)
      if (side === 'front') {
        setFront((current) => {
          if (current) URL.revokeObjectURL(current.previewUrl)
          return item
        })
      } else {
        setBack((current) => {
          if (current) URL.revokeObjectURL(current.previewUrl)
          return item
        })
      }
      setNotice({ tone: 'success', text: '图片已压缩，可以继续上传' })
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : '图片处理失败' })
    } finally {
      setProcessingSide(null)
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    const normalizedWaybill = waybill.trim()
    if (!normalizedWaybill) {
      setNotice({ tone: 'error', text: '请填写运单号' })
      return
    }
    if (!front || !back) {
      setNotice({ tone: 'error', text: '请上传身份证正面和背面照片' })
      return
    }

    setBusy(true)
    setNotice(null)
    try {
      setStepText('正在上传身份证正面')
      const frontUrl = await uploadToOss(front.file)
      setStepText('正在上传身份证背面')
      const backUrl = await uploadToOss(back.file)
      setStepText('正在提交订单身份证照片')
      await postJson('/api/oss/upload-ident-image', {
        waybill: normalizedWaybill,
        front: frontUrl,
        back: backUrl,
      })
      setNotice({ tone: 'success', text: '上传成功，清关资料已提交' })
      setFront((current) => {
        if (current) URL.revokeObjectURL(current.previewUrl)
        return null
      })
      setBack((current) => {
        if (current) URL.revokeObjectURL(current.previewUrl)
        return null
      })
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : '上传失败，请稍后重试' })
    } finally {
      setBusy(false)
      setStepText('')
    }
  }

  return (
    <main className="page-shell">
      <section className="hero">
        <p className="eyebrow">清关资料</p>
        <h1>身份证照片上传</h1>
        <p>
          根据中国海关要求，入境清关需要提交收件人身份证正反面照片。照片只用于订单清关，请确认运单号无误后上传。
        </p>
      </section>

      <form className="upload-flow" onSubmit={handleSubmit}>
        <label className="waybill-field">
          <span>运单号</span>
          <input
            value={waybill}
            autoComplete="off"
            inputMode="text"
            placeholder="请输入运单号"
            disabled={busy}
            onChange={(event) => setWaybill(event.target.value)}
          />
        </label>

        <div className="cards">
          <UploadCard
            title="身份证正面"
            item={front}
            disabled={busy || processingSide !== null}
            onPick={(event) => handlePick('front', event)}
          />
          <UploadCard
            title="身份证背面"
            item={back}
            disabled={busy || processingSide !== null}
            onPick={(event) => handlePick('back', event)}
          />
        </div>

        {notice ? <div className={`notice notice--${notice.tone}`}>{notice.text}</div> : null}
        {stepText ? <div className="progress-text">{stepText}</div> : null}

        <button className="submit-button" type="submit" disabled={!canSubmit}>
          {busy ? '上传中…' : processingSide ? '图片处理中…' : '提交身份证照片'}
        </button>
      </form>

      <footer className="footer-note">
        <a href="http://xian.customs.gov.cn/customs/302249/302266/302267/356308/index.html" target="_blank" rel="noreferrer">
          查看海关清关说明
        </a>
      </footer>
    </main>
  )
}
